import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { Spacer } from "~/components/common/spacer";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { useContentHelper } from "~/components/content/hooks";
import type { opinionContentItemContent$key } from "~/queries/__generated__/opinionContentItemContent.graphql";

import {
  Author,
  AuthorAvatarContainer,
  AuthorNameContainer,
  Container,
  Content,
  Footer,
  Header,
  StyledAuthorAvatar,
  StyledAuthorName,
  StyledAuthorTitle,
  StyledEntityLink,
} from "./styles";

export type Props = {
  reference: opinionContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemOpinion = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment opinionContentItemContent on Content {
          ...hooksContentHelperContent
          ...entityLink
          authors {
            entityId
            ...authorAvatarAuthor
            ...authorNameAuthor
          }
          ...hooksContentItemProvidersContent
            @arguments(withHeadline: true, withSummary: true, withActionBar: true)
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    const { isAuthorSCMPColumnist, isAuthorSCMPEditorial, isEditorialOpinionType } =
      useContentHelper(content);
    const isColumnistArticle = isAuthorSCMPColumnist;
    const isEditorialArticle = isAuthorSCMPEditorial && isEditorialOpinionType;
    const contentAuthors = content?.authors?.slice?.(0, 2) ?? [];

    const authorAvatars = contentAuthors.map(author =>
      author ? (
        <StyledAuthorAvatar
          $isBordered={isEditorialArticle || isAuthorSCMPColumnist}
          height={40}
          key={author.entityId}
          reference={author}
          width={40}
        />
      ) : null,
    );

    const authorNames = contentAuthors.map(author =>
      author && !isEditorialArticle ? (
        <StyledAuthorName key={author.entityId} reference={author} />
      ) : null,
    );

    const specialTitle =
      isEditorialArticle || isAuthorSCMPColumnist ? (
        <StyledAuthorTitle>
          {isEditorialArticle && "SCMP Editorial"}
          {isAuthorSCMPColumnist && "SCMP Columnist"}
        </StyledAuthorTitle>
      ) : null;

    const authors = (
      <>
        <AuthorNameContainer>
          {specialTitle}
          {authorNames}
        </AuthorNameContainer>
        <Spacer />
        <AuthorAvatarContainer>{authorAvatars}</AuthorAvatarContainer>
      </>
    );
    const header = (
      <StyledEntityLink reference={content}>
        {providers.headline({
          hideTooltip: true,
          preferSocialHeadline: true,
          skipHeadlineSponsorStyle: true,
        })}
      </StyledEntityLink>
    );
    const summary = isEditorialArticle || isColumnistArticle ? providers.summary() : null;
    const footer = providers.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Content>{summary}</Content>
        <Author>{authors}</Author>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemOpinion.displayName = "ContentItemOpinion";
